import { LogLevel } from '@empathy/logger';
import { inject, injectable } from 'inversify';
import Vue from 'vue';
import { AppBuilder } from '../builders';
import { EVENTS } from '../bus/events.const';
import { DEPENDENCIES } from '../container/dependencies.const';
import { logger } from '../logger';
import { DynamicConfig } from '../models';
import { UpdateResultMutationPayload } from '../store/search/mutations/update-result.mutation';
import { DeepPartial } from '../utils';
import { ClientDynamicConfig, EmpathyXAPI } from './api.interfaces';

@injectable()
export class DefaultEmpathyXAPI implements EmpathyXAPI {
  protected empathyXInitialized = false;
  protected empathyXRootNode: Vue;

  constructor(
    @inject(DEPENDENCIES.AppBuilder) private readonly appBuilder: AppBuilder
  ) {}

  public init(dynamicConfig: ClientDynamicConfig): void {
    if (!this.empathyXInitialized) {
      this.empathyXInitialized = true;
      this.empathyXRootNode = this.appBuilder.build(dynamicConfig);
    } else {
      logger.error('We know EmpathyX is awesome, but you only need to initialize it once.');
    }
  }

  public rebindDomEvents(): void {
    this.empathyXRootNode.$x.emit(EVENTS.RebindDomEvents);
  }

  public setLocale(localeCode: string, searchLocaleCode: string = localeCode): void {
    this.empathyXRootNode.$x.emit(EVENTS.LocaleChanged, localeCode);
    this.empathyXRootNode.$x.emit(EVENTS.SearchLocaleChanged, searchLocaleCode);
  }

  public setCurrency(currencyCode: string): void {
    this.empathyXRootNode.$x.emit(EVENTS.CurrencyChanged, currencyCode);
  }

  public setConsoleLogLevel(newLevel: LogLevel): void {
    if (newLevel) {
      logger.consoleLevel = newLevel;
    }
  }

  public setServerLogLevel(newLevel: LogLevel): void {
    if (newLevel) {
      logger.serverLevel = newLevel;
    }
  }

  public setConfig(dynamicConfig: DeepPartial<DynamicConfig>): void {
    this.empathyXRootNode.$x.emit(EVENTS.DynamicConfigChanged, dynamicConfig);
  }

  public updatePosition(): void {
    this.empathyXRootNode.$x.emit(EVENTS.WindowSizeChanged);
  }

  public addToCartOnProductPageClicked(): void {
    this.empathyXRootNode.$x.emit(EVENTS.Add2CartOnProductPageClicked);
  }

  public productPage(productId: string): void {
    this.empathyXRootNode.$x.emit(EVENTS.ProductPageDetected, productId);
  }

  public checkout(productsIds: string | string[]): void {
    this.empathyXRootNode.$x.emit(EVENTS.Checkout, productsIds);
  }

  public addToWishlist(payload: UpdateResultMutationPayload): void {
    this.empathyXRootNode.$x.emit(EVENTS.AddToWishlistSuccess, payload);
  }

  public removeFromWishlist(payload: UpdateResultMutationPayload): void {
    this.empathyXRootNode.$x.emit(EVENTS.RemoveFromWishlistSuccess, payload);
  }

  public loadWishlist(wishlist: string[]): void {
    this.empathyXRootNode.$x.emit(EVENTS.GetWishlistSuccess, wishlist);
  }

  public getVersion(): string {
    return EMPATHY_X_VERSION;
  }
}
